import { useQuery, useMutation } from '@tanstack/react-query'
import { useState, useMemo, useEffect } from 'react'
import {
  Search, RefreshCw, Package, XCircle, Loader2, ChevronDown, ChevronUp,
  MapPin, AlertCircle, ExternalLink, Filter, RotateCcw, ArrowUpDown, Printer,
  Truck, History, Download, FileText, PackageCheck
} from 'lucide-react'
import { useToast } from '@/components/ui/Toast'
import { shipping } from '@/lib/api'
import { CarrierLogo, CountryFlag } from '@/components/CarrierLogos'
import ShipmentDetailModal from './ShipmentDetailModal'

// Estados de envío con colores
const STATUS_CONFIG: Record<string, { label: string; bgColor: string; textColor: string; barColor: string }> = {
  pending: { label: 'PENDIENTE', bgColor: 'bg-slate-100', textColor: 'text-slate-600', barColor: 'bg-slate-400' },
  created: { label: 'CREADO', bgColor: 'bg-blue-50', textColor: 'text-blue-700', barColor: 'bg-blue-400' },
  shipped: { label: 'ENVIADO', bgColor: 'bg-indigo-50', textColor: 'text-indigo-700', barColor: 'bg-indigo-400' },
  in_transit: { label: 'EN TRÁNSITO', bgColor: 'bg-teal-50', textColor: 'text-teal-700', barColor: 'bg-teal-500' },
  out_for_delivery: { label: 'EN REPARTO', bgColor: 'bg-amber-50', textColor: 'text-amber-700', barColor: 'bg-amber-500' },
  ready_for_pickup: { label: 'EN PARCELSHOP', bgColor: 'bg-violet-50', textColor: 'text-violet-700', barColor: 'bg-violet-500' },
  delivered: { label: 'ENTREGADO', bgColor: 'bg-lime-100', textColor: 'text-lime-800', barColor: 'bg-lime-600' },
  failed: { label: 'FALLIDO', bgColor: 'bg-red-50', textColor: 'text-red-700', barColor: 'bg-red-500' },
  returned: { label: 'DEVUELTO', bgColor: 'bg-orange-50', textColor: 'text-orange-700', barColor: 'bg-orange-500' },
  cancelled: { label: 'CANCELADO', bgColor: 'bg-slate-100', textColor: 'text-slate-500', barColor: 'bg-slate-400' },
}

// Opciones de filtro
const CARRIERS = [
  { code: '', label: 'Todo' },
  { code: 'mrw', label: 'MRW' },
  { code: 'gls', label: 'GLS' },
  { code: 'ctt', label: 'CTT' },
  { code: 'dhl', label: 'DHL' },
  { code: 'cex', label: 'Correos Express' },
  { code: 'fedex', label: 'FedEx' },
]

const COUNTRIES = [
  { code: '', label: 'Todo' },
  { code: 'ES', label: 'España' },
  { code: 'PT', label: 'Portugal' },
  { code: 'FR', label: 'Francia' },
  { code: 'DE', label: 'Alemania' },
  { code: 'IT', label: 'Italia' },
]

const TIME_RANGES = [
  { value: '', label: 'Todo' },
  { value: 'today', label: 'Hoy' },
  { value: 'yesterday', label: 'Ayer' },
  { value: 'week', label: 'Última semana' },
  { value: 'month', label: 'Último mes' },
]

type SubTab = 'tracking' | 'incidents'

interface Filters {
  carrier: string
  country: string
  status: string
  timeRange: string
}

// Tipos de ordenación
type SortField = 'order_date' | 'shipping_date' | 'order_id' | null
type SortDirection = 'asc' | 'desc'

export default function TrackingTab() {
  const toast = useToast()
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('tracking')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedShipmentId, setSelectedShipmentId] = useState<number | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<Filters>({
    carrier: '',
    country: '',
    status: '',
    timeRange: '',
  })

  // Estado para ocultar entregados
  const [hideDelivered, setHideDelivered] = useState(false)
  // Estado para mostrar solo reembolsos
  const [onlyCOD, setOnlyCOD] = useState(false)

  // Estado de ordenación
  const [sortField, setSortField] = useState<SortField>('shipping_date')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Estado de selección para impresión múltiple
  const [selectedItems, setSelectedItems] = useState<number[]>([])

  // Debounce para búsqueda en backend (busca en todo el histórico)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Función para cambiar ordenación
  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      // Si es el mismo campo, cambiar dirección
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      // Si es otro campo, establecerlo con orden descendente por defecto
      setSortField(field)
      setSortDirection('desc')
    }
  }

  // Query para envíos con tracking
  // Usa el nuevo endpoint que consulta directamente la BD de envíos
  // Cuando hay búsqueda, consulta TODO el histórico en el backend
  const shipmentsQuery = useQuery({
    queryKey: ['shipping-tracking', debouncedSearch],
    queryFn: async () => {
      const response = await shipping.listOrdersWithShipments({
        exclude_cancelled: true,
        limit: 500,
        search: debouncedSearch || undefined
      })
      // El endpoint ya filtra por envíos válidos, pero verificamos tracking_number
      return response.orders.filter(o =>
        o.shipment?.tracking_number
      )
    },
  })

  const shipments = shipmentsQuery.data || []

  // Función para verificar si está en rango de tiempo
  const isInTimeRange = (dateStr: string, range: string) => {
    if (!range) return true
    const date = new Date(dateStr)
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000)
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)

    switch (range) {
      case 'today': return date >= today
      case 'yesterday': return date >= yesterday && date < today
      case 'week': return date >= weekAgo
      case 'month': return date >= monthAgo
      default: return true
    }
  }

  // Filtrar por filtros locales (la búsqueda ya se hace en el backend)
  const filteredShipments = shipments.filter(s => {
    // Filtro de ocultar entregados
    if (hideDelivered && s.shipment?.status === 'delivered') {
      return false
    }

    // Filtro de solo reembolsos (forma de pago = contrareembolso)
    if (onlyCOD && !s.is_cod) {
      return false
    }

    // Filtro de transportista
    if (filters.carrier && s.shipment?.carrier_code !== filters.carrier) {
      return false
    }

    // Filtro de país
    if (filters.country && s.shipping_address?.country_code !== filters.country) {
      return false
    }

    // Filtro de estado
    if (filters.status && s.shipment?.status !== filters.status) {
      return false
    }

    // Filtro de rango de tiempo
    if (filters.timeRange && !isInTimeRange(s.shipment?.created_at || s.date_add, filters.timeRange)) {
      return false
    }

    return true
  })

  // Separar incidentes
  const incidents = filteredShipments.filter(s =>
    s.shipment?.status === 'failed' || s.shipment?.status === 'returned'
  )

  // Ordenar envíos
  const sortedShipments = useMemo(() => {
    const shipmentsToSort = activeSubTab === 'incidents' ? incidents : filteredShipments

    if (!sortField) return shipmentsToSort

    return [...shipmentsToSort].sort((a, b) => {
      let valueA: number
      let valueB: number

      switch (sortField) {
        case 'order_date':
          valueA = new Date(a.date_add).getTime()
          valueB = new Date(b.date_add).getTime()
          break
        case 'shipping_date':
          valueA = new Date(a.shipment?.shipped_at || a.shipment?.created_at || 0).getTime()
          valueB = new Date(b.shipment?.shipped_at || b.shipment?.created_at || 0).getTime()
          break
        case 'order_id':
          valueA = a.id
          valueB = b.id
          break
        default:
          return 0
      }

      const comparison = valueA - valueB
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [filteredShipments, incidents, activeSubTab, sortField, sortDirection])

  const displayShipments = sortedShipments

  const selectedOrder = shipments.find(s => s.id === selectedShipmentId)

  // Reset filtros
  const resetFilters = () => {
    setFilters({ carrier: '', country: '', status: '', timeRange: '' })
  }

  // Verificar si hay filtros activos
  const hasActiveFilters = filters.carrier || filters.country || filters.status || filters.timeRange

  // Funciones de selección
  const handleSelectAll = () => {
    if (selectedItems.length === displayShipments.length && displayShipments.length > 0) {
      setSelectedItems([])
    } else {
      // Seleccionar solo los que tienen shipment.id válido
      setSelectedItems(displayShipments.filter(s => s.shipment?.id).map(s => s.shipment!.id))
    }
  }

  const handleSelectItem = (shipmentId: number) => {
    setSelectedItems(prev =>
      prev.includes(shipmentId)
        ? prev.filter(id => id !== shipmentId)
        : [...prev, shipmentId]
    )
  }

  // Mutation para imprimir etiquetas combinadas
  const printLabelsMutation = useMutation({
    mutationFn: async (shipmentIds: number[]) => {
      return await shipping.getCombinedLabels(shipmentIds)
    },
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 60000)
      toast.success(`${selectedItems.length} etiqueta${selectedItems.length > 1 ? 's' : ''} abierta${selectedItems.length > 1 ? 's' : ''}`)
    },
    onError: (error: Error) => {
      toast.error(`Error al obtener etiquetas: ${error.message}`)
    }
  })

  return (
    <div className="flex h-full">
      {/* Contenido principal */}
      <div className="flex-1 flex flex-col">
        {/* Sub-tabs: Seguimiento / Incidentes */}
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6">
          <div className="flex items-center justify-between py-3">
            {/* Sub-tabs */}
            <div className="flex items-center gap-6">
              <button
                onClick={() => setActiveSubTab('tracking')}
                className={`flex items-center gap-2 pb-2 border-b-2 transition-colors ${
                  activeSubTab === 'tracking'
                    ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <MapPin className="w-4 h-4" />
                <span className="font-medium">Seguimiento</span>
              </button>
              <button
                onClick={() => setActiveSubTab('incidents')}
                className={`flex items-center gap-2 pb-2 border-b-2 transition-colors ${
                  activeSubTab === 'incidents'
                    ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <AlertCircle className="w-4 h-4" />
                <span className="font-medium">Incidentes</span>
                {incidents.length > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-600 rounded-full">
                    {incidents.length}
                  </span>
                )}
              </button>
            </div>

            {/* Búsqueda y acciones */}
            <div className="flex items-center gap-3">
              {/* Checkbox ocultar entregados */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={hideDelivered}
                  onChange={(e) => setHideDelivered(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                />
                <span className="text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                  Ocultar entregados
                </span>
              </label>

              {/* Checkbox solo reembolsos */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={onlyCOD}
                  onChange={(e) => setOnlyCOD(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                />
                <span className="text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                  Solo reembolsos
                </span>
              </label>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar en todo el histórico..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 w-72 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Botón de filtros */}
              <div className="relative">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                    hasActiveFilters
                      ? 'bg-teal-50 border-teal-300 text-teal-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  <span className="text-sm font-medium">Filtros</span>
                  {hasActiveFilters && (
                    <span className="flex items-center justify-center w-5 h-5 text-xs bg-teal-600 text-white rounded-full">
                      {[filters.carrier, filters.country, filters.status, filters.timeRange].filter(Boolean).length}
                    </span>
                  )}
                </button>

                {/* Panel de filtros desplegable */}
                {showFilters && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowFilters(false)} />
                    <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Filtrar envíos</h3>
                        {hasActiveFilters && (
                          <button
                            onClick={resetFilters}
                            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
                          >
                            <RotateCcw className="w-3 h-3" />
                            Limpiar
                          </button>
                        )}
                      </div>

                      <div className="space-y-4">
                        {/* Transportista */}
                        <div>
                          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                            Transportista
                          </label>
                          <select
                            value={filters.carrier}
                            onChange={(e) => setFilters(f => ({ ...f, carrier: e.target.value }))}
                            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          >
                            {CARRIERS.map(c => (
                              <option key={c.code} value={c.code}>{c.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* País destino */}
                        <div>
                          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                            País destino
                          </label>
                          <select
                            value={filters.country}
                            onChange={(e) => setFilters(f => ({ ...f, country: e.target.value }))}
                            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          >
                            {COUNTRIES.map(c => (
                              <option key={c.code} value={c.code}>{c.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Estado */}
                        <div>
                          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                            Estado del envío
                          </label>
                          <select
                            value={filters.status}
                            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
                            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          >
                            <option value="">Todo</option>
                            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                              <option key={key} value={key}>{config.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Rango de tiempo */}
                        <div>
                          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                            Fecha de envío
                          </label>
                          <select
                            value={filters.timeRange}
                            onChange={(e) => setFilters(f => ({ ...f, timeRange: e.target.value }))}
                            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          >
                            {TIME_RANGES.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Botón aplicar */}
                      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <button
                          onClick={() => setShowFilters(false)}
                          className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
                        >
                          Aplicar filtros
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <button
                onClick={() => shipmentsQuery.refetch()}
                disabled={shipmentsQuery.isFetching}
                className="p-2 text-slate-500 hover:text-slate-700 rounded-lg hover:bg-slate-100"
                title="Actualizar"
              >
                <RefreshCw className={`w-5 h-5 ${shipmentsQuery.isFetching ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Tabla de seguimiento */}
        <div className="flex-1 overflow-auto p-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-[40px_80px_60px_75px_75px_100px_140px_75px_85px_75px_100px_40px] gap-2 px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 text-xs font-medium text-slate-500 uppercase">
              {/* Checkbox select all */}
              <div className="flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={selectedItems.length === displayShipments.length && displayShipments.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                />
              </div>
              <div>Mensajería</div>
              <div>Destino</div>
              <button
                onClick={() => toggleSort('order_date')}
                className={`flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300 transition-colors ${
                  sortField === 'order_date' ? 'text-teal-600 dark:text-teal-400' : ''
                }`}
              >
                F. Pedido
                {sortField === 'order_date' ? (
                  sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                ) : (
                  <ArrowUpDown className="w-3 h-3 opacity-50" />
                )}
              </button>
              <button
                onClick={() => toggleSort('shipping_date')}
                className={`flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300 transition-colors ${
                  sortField === 'shipping_date' ? 'text-teal-600 dark:text-teal-400' : ''
                }`}
              >
                F. Envío
                {sortField === 'shipping_date' ? (
                  sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                ) : (
                  <ArrowUpDown className="w-3 h-3 opacity-50" />
                )}
              </button>
              <button
                onClick={() => toggleSort('order_id')}
                className={`flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300 transition-colors ${
                  sortField === 'order_id' ? 'text-teal-600 dark:text-teal-400' : ''
                }`}
              >
                ID Pedido
                {sortField === 'order_id' ? (
                  sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                ) : (
                  <ArrowUpDown className="w-3 h-3 opacity-50" />
                )}
              </button>
              <div>Tracking</div>
              <div>Total</div>
              <div>Pago</div>
              <div>Reembolso Etiq.</div>
              <div>Estado</div>
              <div></div>
            </div>

            {/* Contenido */}
            {shipmentsQuery.isLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-teal-600 animate-spin mb-3" />
                <p className="text-slate-500">Cargando envíos...</p>
              </div>
            ) : shipmentsQuery.isError ? (
              <div className="flex flex-col items-center justify-center py-16">
                <XCircle className="w-8 h-8 text-red-500 mb-3" />
                <p className="text-red-600">Error cargando envíos</p>
              </div>
            ) : displayShipments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Package className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-slate-500">
                  {activeSubTab === 'incidents' ? 'No hay incidentes' : 'No hay envíos en seguimiento'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                {displayShipments.map((order) => {
                  const shipment = order.shipment!
                  const status = STATUS_CONFIG[shipment.status] || STATUS_CONFIG.pending
                  const isSelected = shipment.id ? selectedItems.includes(shipment.id) : false
                  const codAmountSent = shipment.cod_amount_sent || 0
                  const orderTotal = order.total || 0
                  // Verificar si COD coincide con total (con tolerancia de 0.01)
                  const codMismatch = codAmountSent > 0 && Math.abs(codAmountSent - orderTotal) > 0.01

                  return (
                    <div
                      key={order.id}
                      className={`grid grid-cols-[40px_80px_60px_75px_75px_100px_140px_75px_85px_75px_100px_40px] gap-2 px-4 py-3 items-center hover:bg-slate-50 dark:hover:bg-slate-900/30 cursor-pointer ${
                        isSelected ? 'bg-teal-50 dark:bg-teal-900/20' : ''
                      }`}
                      onClick={() => setSelectedShipmentId(order.id)}
                    >
                      {/* Checkbox de selección */}
                      <div
                        className="flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => shipment.id && handleSelectItem(shipment.id)}
                          className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                        />
                      </div>

                      {/* Mensajería (Logo) */}
                      <div>
                        <CarrierLogo carrier={shipment.carrier_code} className="h-5 w-14" />
                      </div>

                      {/* Destino */}
                      <div className="flex items-center gap-1">
                        <CountryFlag countryCode={order.shipping_address.country_code} />
                        <span className="text-xs text-slate-600 dark:text-slate-400">
                          {order.shipping_address.country_code}
                        </span>
                      </div>

                      {/* Fecha pedido */}
                      <div className="text-xs text-slate-600 dark:text-slate-400">
                        <div>{new Date(order.date_add).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</div>
                        <div>{new Date(order.date_add).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}</div>
                      </div>

                      {/* Fecha envío */}
                      <div className="text-xs text-slate-600 dark:text-slate-400">
                        {shipment.shipped_at ? (
                          <>
                            <div>{new Date(shipment.shipped_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</div>
                            <div>{new Date(shipment.shipped_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}</div>
                          </>
                        ) : (
                          <>
                            <div>{new Date(shipment.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</div>
                            <div>{new Date(shipment.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}</div>
                          </>
                        )}
                      </div>

                      {/* ID del pedido */}
                      <div>
                        {order.is_manual && order.custom_reference ? (
                          <>
                            <div className="text-sm font-medium text-slate-900 dark:text-white truncate" title={order.custom_reference}>{order.custom_reference}</div>
                            <div className="text-xs text-slate-500" title={order.reference}>{order.reference}</div>
                          </>
                        ) : (
                          <>
                            <div className="text-sm font-medium text-slate-900 dark:text-white truncate" title={order.reference}>{order.reference}</div>
                            <div className="text-xs text-slate-500">#{order.id}</div>
                          </>
                        )}
                      </div>

                      {/* Seguimiento courier */}
                      <div className="text-xs font-mono text-slate-600 dark:text-slate-400 truncate" title={shipment.tracking_number}>
                        {shipment.tracking_number}
                      </div>

                      {/* Total del pedido */}
                      <div className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        {orderTotal.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                      </div>

                      {/* Forma de pago */}
                      <div className="text-xs text-slate-600 dark:text-slate-400 truncate" title={order.payment_method}>
                        {order.is_cod ? (
                          <span className="text-amber-600 dark:text-amber-400 font-medium">Contrareemb.</span>
                        ) : (
                          order.payment_method?.replace('Pago con ', '').substring(0, 12) || '-'
                        )}
                      </div>

                      {/* COD en etiqueta */}
                      <div className="text-xs">
                        {codAmountSent > 0 ? (
                          <span className={`font-medium ${codMismatch ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                            {codAmountSent.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                            {codMismatch && ' ⚠️'}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </div>

                      {/* Estado */}
                      <div className="flex items-center gap-1">
                        <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${status.bgColor} ${status.textColor}`}>
                          {status.label}
                        </span>
                        <div className={`w-1 h-6 rounded-full ${status.barColor}`} />
                      </div>

                      {/* Acciones rápidas */}
                      <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                        {shipment.tracking_url && (
                          <a
                            href={shipment.tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 text-slate-400 hover:text-teal-600"
                            title="Ver tracking"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Panel lateral - Estadísticas */}
      <div className="w-72 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 p-5 space-y-6">
        {/* Acciones de selección */}
        {selectedItems.length > 0 && (
          <div className="space-y-3 pb-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                Seleccionados
              </h3>
              <span className="px-2 py-0.5 text-xs font-medium bg-teal-100 text-teal-700 rounded-full">
                {selectedItems.length}
              </span>
            </div>
            <button
              onClick={() => printLabelsMutation.mutate(selectedItems)}
              disabled={printLabelsMutation.isPending}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {printLabelsMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Printer className="w-4 h-4" />
              )}
              Imprimir {selectedItems.length} etiqueta{selectedItems.length > 1 ? 's' : ''}
            </button>
            <button
              onClick={() => setSelectedItems([])}
              className="w-full px-4 py-2 text-sm text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Limpiar selección
            </button>
          </div>
        )}

        {/* Estadísticas rápidas */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Resumen
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
              <span className="text-sm text-slate-600 dark:text-slate-400">En tránsito</span>
              <span className="text-lg font-semibold text-teal-600">
                {shipments.filter(s => s.shipment?.status === 'in_transit').length}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
              <span className="text-sm text-slate-600 dark:text-slate-400">En reparto</span>
              <span className="text-lg font-semibold text-amber-600">
                {shipments.filter(s => s.shipment?.status === 'out_for_delivery').length}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
              <span className="text-sm text-slate-600 dark:text-slate-400">Entregados hoy</span>
              <span className="text-lg font-semibold text-emerald-600">
                {shipments.filter(s => {
                  if (s.shipment?.status !== 'delivered') return false
                  const deliveredDate = s.shipment?.delivered_at || s.shipment?.shipped_at
                  if (!deliveredDate) return false
                  const today = new Date()
                  const shipmentDate = new Date(deliveredDate)
                  return shipmentDate.toDateString() === today.toDateString()
                }).length}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
              <span className="text-sm text-slate-600 dark:text-slate-400">Total entregados</span>
              <span className="text-lg font-semibold text-emerald-600">
                {shipments.filter(s => s.shipment?.status === 'delivered').length}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
              <span className="text-sm text-slate-600 dark:text-slate-400">Incidentes</span>
              <span className="text-lg font-semibold text-red-600">
                {incidents.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de detalle */}
      {selectedOrder && (
        <ShipmentDetailModal
          order={selectedOrder}
          onClose={() => setSelectedShipmentId(null)}
        />
      )}
    </div>
  )
}
